import { createFileRoute, notFound } from "@tanstack/react-router";
import { getPage } from "@/content/pages";
import { MarkdownPage } from "@/components/MarkdownPage";
import ServiceWebDesignImage from "@/assets/service-web-design.jpg";
import { breadcrumbScript, ORGANIZATION_ID, SITE_URL } from "@/lib/seo";

const PAGE_KEY = "/web-design";

export const Route = createFileRoute("/web-design")({
  loader: () => {
    const page = getPage(PAGE_KEY);
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData }) => {
    const url = `${SITE_URL}${PAGE_KEY}`;
    return {
      meta: loaderData
        ? [
            { title: loaderData.title },
            { name: "description", content: loaderData.description },
            { property: "og:title", content: loaderData.title },
            { property: "og:description", content: loaderData.description },
            { property: "og:url", content: url },
          ]
        : [],
      links: [{ rel: "canonical", href: url }],
      scripts: loaderData
        ? [
            breadcrumbScript([
              { name: "Home", url: `${SITE_URL}/` },
              { name: "Web design", url },
            ]),
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                name: "Web Design Barnsley",
                description: loaderData.description,
                serviceType: [
                  "Business website design",
                  "WordPress website design",
                  "Ecommerce website design",
                  "Landing page design",
                  "Website redesign",
                ],
                provider: { "@id": ORGANIZATION_ID },
                areaServed: ["Barnsley", "South Yorkshire", "Yorkshire", "United Kingdom"],
              }),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Page not found</h1>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: Page,
});

function Page() {
  const page = Route.useLoaderData();
  return (
    <MarkdownPage
      body={page.body}
      pageContext={page.title}
      heroImage={{
        src: ServiceWebDesignImage,
        alt: "Web design wireframe sketches and colour swatches on a wooden desk",
      }}
      relatedSlugs={[
        "building-a-website-that-supports-seo-and-business-growth",
        "why-seo-starts-with-your-website-and-always-will",
        "turning-website-visitors-into-customers-with-seo",
      ]}
    />
  );
}
